import React, { useState } from 'react';
import axios from 'axios';
import './Contact.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const services = [
  'NIOS Registration & Coaching',
  'Special Education',
  'Learning Disability Management',
  'Remedial Teaching',
  'Student Counselling',
  'Behavior Therapy',
  'Other / Not Sure',
];

const MAPS_LINK = 'https://maps.app.goo.gl/E5xYGoywiRFwwRQR7';
const ADDRESS   = 'Plot No:117, Anna Nagar Rd, Anna Nagar, HL Colony, Pammal, Chennai, Tamil Nadu 600075';

const contactCards = [
  {
    icon: '📧',
    label: 'Email',
    lines: ['info@aksharalearningcentre.in'],
    link: 'mailto:info@aksharalearningcentre.in',
    linkLabel: 'Send a message →',
  },
  {
    icon: '📱',
    label: 'Phone / WhatsApp',
    lines: ['+91 97500 67070', '+91 73582 70743'],
    link: 'tel:+919750067070',
    linkLabel: 'Call now →',
  },
  {
    icon: '📍',
    label: 'Address',
    lines: [ADDRESS],
    link: MAPS_LINK,
    linkLabel: 'Open in Google Maps →',
    external: true,
  },
  {
    icon: '🕐',
    label: 'Working Hours',
    lines: ['Mon – Sat: 10 AM – 6 PM', 'Sunday: By Appointment'],
  },
];

const initialForm = {
  studentName: '', parentName: '', phone: '', email: '', service: '', message: '',
};

export default function Contact() {
  const [form, setForm]     = useState(initialForm);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.studentName || !form.parentName || !form.phone) {
      setErrorMsg('Please fill in Student Name, Parent Name, and Phone.');
      setStatus('error');
      return;
    }
    try {
      setStatus('loading');
      await axios.post(`${API_URL}/inquiry`, form);
      setStatus('success');
      setForm(initialForm);
      setTimeout(() => setStatus('idle'), 6000);
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  return (
    <section className="contact reveal" id="contact">
      <div className="contact-inner">

        {/* ── Left column ── */}
        <div className="contact-left">
          <span className="section-badge contact-badge">Contact Us</span>
          <h2 className="section-title contact-title">Start Your Child's Journey Today</h2>
          <p className="contact-sub">
            Reach out to discuss how we can support your child's unique educational needs.
            We respond to all enquiries within 24 hours.
          </p>

          <div className="contact-cards">
            {contactCards.map((c) => (
              <div className="contact-card" key={c.label}>
                <div className="cc-icon">{c.icon}</div>
                <div className="cc-body">
                  <div className="cc-label">{c.label}</div>
                  {c.lines.map((l, i) => (
                    <div className="cc-value" key={i}>{l}</div>
                  ))}
                  {c.link && (
                    <a
                      href={c.link}
                      className="cc-link"
                      target={c.external ? '_blank' : undefined}
                      rel={c.external ? 'noopener noreferrer' : undefined}
                    >
                      {c.linkLabel}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right column: form + map ── */}
        <div className="contact-right">

          {/* Enquiry Form */}
          <form className="contact-form" onSubmit={onSubmit} noValidate>
            <h3 className="form-heading">Send an Enquiry</h3>

            <div className="form-row">
              <div className="form-group">
                <label>Student Name *</label>
                <input name="studentName" value={form.studentName} onChange={onChange} placeholder="e.g. Arjun Kumar" />
              </div>
              <div className="form-group">
                <label>Parent / Guardian Name *</label>
                <input name="parentName" value={form.parentName} onChange={onChange} placeholder="e.g. Mrs. Kumar" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Phone *</label>
                <input name="phone" type="tel" value={form.phone} onChange={onChange} placeholder="+91 XXXXX XXXXX" />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input name="email" type="email" value={form.email} onChange={onChange} placeholder="email@example.com" />
              </div>
            </div>

            <div className="form-group">
              <label>Programme of Interest</label>
              <select name="service" value={form.service} onChange={onChange}>
                <option value="">Select a programme…</option>
                {services.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Message / Concerns</label>
              <textarea
                name="message" value={form.message} onChange={onChange} rows={3}
                placeholder="Tell us about your child's needs…"
              />
            </div>

            <button className="submit-btn" type="submit" disabled={status === 'loading'}>
              {status === 'loading' ? 'Sending…' : 'Send Enquiry →'}
            </button>

            {status === 'success' && (
              <div className="form-feedback success">✅ Thank you! We'll contact you within 24 hours.</div>
            )}
            {status === 'error' && (
              <div className="form-feedback error">⚠️ {errorMsg}</div>
            )}
          </form>

          {/* Google Map Embed */}
          <div className="map-wrap">
            <div className="map-header">
              <span className="map-icon">📍</span>
              <div>
                <p className="map-title">Find Us Here</p>
                <p className="map-address">{ADDRESS}</p>
              </div>
            </div>
            <div className="map-frame-wrap">
              <iframe
                title="Akshara Learning Centre Location"
                src="https://maps.google.com/maps?q=Plot+No+117+Anna+Nagar+Rd+HL+Colony+Pammal+Chennai+Tamil+Nadu+600075&output=embed&z=16"
                className="map-frame"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <a
              href={MAPS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="map-open-btn"
            >
              Open in Google Maps →
            </a>
          </div>

        </div>
      </div>
    </section>
  );
}
